import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
const API = "/api";

const s = {
  input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "7px 10px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" as const },
  label: { fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4, fontWeight: 500 },
  btn: { border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 500 },
  section: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16, marginBottom: 12 },
};

interface OfferEditProps {
  offerId: string;
  accountId: string;
  accountName: string;
  onClose: () => void;
  onSaved?: () => void;
}

export default function OfferEdit({ offerId, accountId, accountName, onClose, onSaved }: OfferEditProps) {
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [offerData, setOfferData] = useState<any>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, string>>({});

  const [title, setTitle] = useState("");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [handlingTime, setHandlingTime] = useState("PT0S");
  const [status, setStatus] = useState("ACTIVE");
  const [uploadingImages, setUploadingImages] = useState(false);

  const [offerParams, setOfferParams] = useState<any[]>([]);
  const [productParams, setProductParams] = useState<any[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, any>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem("token") || "";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadOffer(); }, [offerId]);
  // Ustaw treść opisu w div po załadowaniu - bez dangerouslySetInnerHTML
  // żeby kursor nie wracał na początek przy każdym znaku
  useEffect(() => {
    if (!loading && descRef.current) {
      descRef.current.innerHTML = description;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const loadOffer = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/offers/${offerId}/full?account_id=${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = r.data;
      setOfferData(d);
      setTitle(d.name || "");
      setSku(d.external?.id || "");
      setPrice(d.sellingMode?.price?.amount || "");
      setStock(String(d.stock?.available || ""));
      setHandlingTime(d.delivery?.handlingTime || "PT0S");
      setStatus(d.publication?.status || "ACTIVE");
      const descContent = d.description?.sections?.[0]?.items?.[0]?.content || "";
      setDescription(descContent);
      setImages(d.images || []);
      const op = d.parameters || [];
      setOfferParams(op);
      const pp = d.productSet?.[0]?.product?.parameters || [];
      setProductParams(pp);
      const vals: Record<string, string> = {};
      [...op, ...pp].forEach((p: any) => {
        const v = p.values?.[0] || p.valuesIds?.[0] || "";
        vals[`${p.id}`] = String(v);
      });
      setParamValues(vals);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const saveSection = async (section: string, patchData: any) => {
    setSaving(section);
    setMessages(prev => ({ ...prev, [section]: "" }));
    try {
      await axios.patch(`${API}/offers/${offerId}/section`, {
        account_id: accountId,
        patch_data: patchData
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => ({ ...prev, [section]: "✓ Zapisano" }));
      if (onSaved) onSaved();
      setTimeout(() => setMessages(prev => ({ ...prev, [section]: "" })), 3000);
    } catch (e: any) {
      const err = e?.response?.data?.detail || e?.response?.data?.errors?.[0]?.userMessage || e?.response?.data?.message || "Błąd zapisu";
      setMessages(prev => ({ ...prev, [section]: "✗ " + err }));
    }
    setSaving(null);
  };

  const handleImageUpload = async (files: FileList) => {
    if (!files.length) return;
    setUploadingImages(true);
    const newUrls: string[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const fd = new FormData();
        fd.append("account_id", accountId);
        fd.append("file", files[i]);
        const r = await axios.post(`${API}/offers/upload-image`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
        newUrls.push(r.data.url);
      } catch (e) {
        setMessages(prev => ({ ...prev, images: "✗ Błąd uploadu zdjęcia " + (i + 1) }));
      }
    }
    if (newUrls.length > 0) setImages(prev => [...prev, ...newUrls]);
    setUploadingImages(false);
  };

  const moveImage = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= images.length) return;
    setImages(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const setMainImage = (idx: number) => {
    if (idx === 0) return;
    setImages(prev => {
      const arr = [...prev];
      const [main] = arr.splice(idx, 1);
      return [main, ...arr];
    });
  };

  const removeImage = (idx: number) => {
    setImages(prev => prev.filter((_, j) => j !== idx));
  };

  const buildParamPayload = (p: any) => {
    const val = paramValues[p.id];
    if (p.restrictions?.range === true) {
      if (val && (val.from || val.to)) {
        const rangeValue: any = {};
        if (val.from) rangeValue.from = val.from;
        if (val.to) rangeValue.to = val.to;
        return { id: p.id, rangeValue };
      }
      return { id: p.id, values: [] };
    }
    return { id: p.id, values: val ? [val] : [] };
  };
  const saveParams = async () => {
    const builtOfferParams = offerParams.map(buildParamPayload).filter((p: any) => (p.values && p.values.length > 0) || p.rangeValue);
    const builtProductParams = productParams.map(buildParamPayload).filter((p: any) => (p.values && p.values.length > 0) || p.rangeValue);
    const patchData: any = {};
    if (builtOfferParams.length > 0) patchData.parameters = builtOfferParams;
    if (builtProductParams.length > 0) {
      patchData.productSet = [{ product: { parameters: builtProductParams } }];
    }
    await saveSection("params", patchData);
  };

  const HANDLING_OPTIONS = [
    { value: "PT0S", label: "Natychmiast" },
    { value: "PT24H", label: "1 dzień" },
    { value: "PT48H", label: "2 dni" },
    { value: "PT72H", label: "3 dni" },
    { value: "PT96H", label: "4 dni" },
    { value: "PT120H", label: "5 dni" },
    { value: "PT168H", label: "7 dni" },
  ];

  const MsgSpan = ({ k }: { k: string }) => messages[k]
    ? <span style={{ fontSize: 12, color: messages[k].startsWith("✓") ? "#15803d" : "#dc2626" }}>{messages[k]}</span>
    : null;

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 40, fontSize: 14 }}>⏳ Ładowanie oferty...</div>
    </div>
  );

  const allParams = [
    ...offerParams.map((p: any) => ({ ...p, _source: "offer" })),
    ...productParams.map((p: any) => ({ ...p, _source: "product" }))
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, overflowY: "auto" }}>
      <div style={{ background: "#f9fafb", borderRadius: 12, padding: 20, maxWidth: 760, margin: "40px auto", position: "relative" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>✏️ Edycja oferty</div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>{accountName} · ID: {offerId}</div>
          </div>
          <button onClick={onClose} style={{ ...s.btn, background: "#f3f4f6", color: "#374151" }}>✕ Zamknij</button>
        </div>

        {/* STATUS */}
        <div style={s.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <label style={s.label}>Status oferty</label>
              <select style={{ ...s.input, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="ACTIVE">Aktywna</option>
                <option value="ENDED">Zakończona</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MsgSpan k="status" />
              <button onClick={() => saveSection("status", { publication: { status } })}
                disabled={saving === "status"}
                style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
                {saving === "status" ? "⏳..." : "Zapisz"}
              </button>
            </div>
          </div>
        </div>

        {/* TYTUL */}
        <div style={s.section}>
          <label style={s.label}>Tytuł oferty *</label>
          <input style={s.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Tytuł oferty" />
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8 }}>
            <MsgSpan k="title" />
            <button onClick={() => saveSection("title", { name: title })}
              disabled={saving === "title"}
              style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
              {saving === "title" ? "⏳..." : "Zapisz tytuł"}
            </button>
          </div>
        </div>
        {/* SKU */}
        <div style={s.section}>
          <label style={s.label}>SKU</label>
          <input style={s.input} value={sku} onChange={e => setSku(e.target.value)} placeholder="Twój numer katalogowy (SKU)" />
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8 }}>
            <MsgSpan k="sku" />
            <button onClick={() => saveSection("sku", { external: { id: sku } })}
              disabled={saving === "sku"}
              style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
              {saving === "sku" ? "⏳..." : "Zapisz SKU"}
            </button>
          </div>
        </div>

        {/* CENA I STAN */}
        <div style={s.section}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <label style={s.label}>Cena (PLN)</label>
              <input type="number" style={s.input} value={price} onChange={e => setPrice(e.target.value)} />
            </div>
            <div>
              <label style={s.label}>Stan (szt.)</label>
              <input type="number" style={s.input} value={stock} onChange={e => setStock(e.target.value)} />
            </div>
            <div>
              <label style={s.label}>Czas wysyłki</label>
              <select style={s.input} value={handlingTime} onChange={e => setHandlingTime(e.target.value)}>
                {HANDLING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8 }}>
            <MsgSpan k="pricing" />
            <button onClick={() => saveSection("pricing", {
              sellingMode: { price: { amount: price, currency: "PLN" } },
              stock: { available: parseInt(stock), unit: "UNIT" },
              delivery: { handlingTime }
            })} disabled={saving === "pricing"}
              style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
              {saving === "pricing" ? "⏳..." : "Zapisz cenę i stan"}
            </button>
          </div>
        </div>

        {/* ZDJECIA */}
        <div style={s.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label style={{ ...s.label, marginBottom: 0 }}>Zdjęcia ({images.length}) — pierwsze = główne</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={e => e.target.files && handleImageUpload(e.target.files)} />
              <button onClick={() => fileInputRef.current?.click()} disabled={uploadingImages}
                style={{ ...s.btn, background: "#059669", color: "#fff" }}>
                {uploadingImages ? "⏳ Uploading..." : "＋ Dodaj zdjęcia"}
              </button>
            </div>
          </div>
          <div
            style={{ border: "2px dashed #d1d5db", borderRadius: 8, padding: 8, minHeight: 60, marginBottom: 8 }}
            onDragOver={e => { e.preventDefault(); (e.currentTarget as HTMLDivElement).style.borderColor = "#1d4ed8"; }}
            onDragLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "#d1d5db"; }}
            onDrop={e => {
              e.preventDefault();
              (e.currentTarget as HTMLDivElement).style.borderColor = "#d1d5db";
              if (e.dataTransfer.files) handleImageUpload(e.dataTransfer.files);
            }}>
            {images.length === 0 && !uploadingImages && (
              <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 12, padding: "16px 0" }}>
                Przeciągnij zdjęcia tutaj lub kliknij „Dodaj zdjęcia"
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {images.map((url, i) => (
                <div key={i} style={{
                  position: "relative",
                  border: i === 0 ? "2px solid #1d4ed8" : "2px solid #e5e7eb",
                  borderRadius: 8, background: "#fff"
                }}>
                  {i === 0 && (
                    <div style={{
                      position: "absolute", top: -8, left: 4, background: "#1d4ed8", color: "#fff",
                      fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "1px 5px", zIndex: 2
                    }}>GŁÓWNE</div>
                  )}
                  <img src={url.replace("/original/", "/s200/")} alt=""
                    style={{ width: 88, height: 88, objectFit: "cover", borderRadius: 6, display: "block" }} />
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    background: "rgba(0,0,0,0.55)", borderRadius: "0 0 6px 6px",
                    display: "flex", justifyContent: "center", gap: 2, padding: "3px 2px"
                  }}>
                    {i !== 0 && (
                      <button title="Ustaw jako główne" onClick={() => setMainImage(i)}
                        style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 3, width: 20, height: 20, cursor: "pointer", fontSize: 11 }}>★</button>
                    )}
                    <button title="Przesuń w lewo" onClick={() => moveImage(i, -1)} disabled={i === 0}
                      style={{ background: i === 0 ? "#6b7280" : "#374151", color: "#fff", border: "none", borderRadius: 3, width: 20, height: 20, cursor: i === 0 ? "default" : "pointer", fontSize: 13 }}>‹</button>
                    <button title="Przesuń w prawo" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1}
                      style={{ background: i === images.length - 1 ? "#6b7280" : "#374151", color: "#fff", border: "none", borderRadius: 3, width: 20, height: 20, cursor: i === images.length - 1 ? "default" : "pointer", fontSize: 13 }}>›</button>
                    <button title="Usuń zdjęcie" onClick={() => removeImage(i)}
                      style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: 3, width: 20, height: 20, cursor: "pointer", fontSize: 11 }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <MsgSpan k="images" />
            <button onClick={() => saveSection("images", { images })}
              disabled={saving === "images" || images.length === 0}
              style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
              {saving === "images" ? "⏳..." : "Zapisz zdjęcia"}
            </button>
          </div>
        </div>

        {/* PARAMETRY */}
        {allParams.length > 0 && (
          <div style={s.section}>
            <label style={{ ...s.label, fontSize: 13, marginBottom: 12 }}>Parametry ({allParams.length})</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {allParams.map((p: any) => {
                const options = p.dictionary || [];
                const val = paramValues[p.id] || "";
                return (
                  <div key={p.id}>
                    <label style={s.label}>
                      {p.name}{p.required && <span style={{ color: "#dc2626" }}> *</span>}
                      {p.unit && <span style={{ color: "#9ca3af", fontWeight: 400, marginLeft: 4 }}>({p.unit})</span>}
                    </label>
                    {options.length > 0 ? (
                      <select style={s.input} value={val}
                        onChange={e => setParamValues(prev => ({ ...prev, [p.id]: e.target.value }))}>
                        <option value="">— wybierz —</option>
                        {options.map((o: any) => (
                          <option key={o.id || o.value} value={o.id || o.value}>{o.value || o.name}</option>
                        ))}
                      </select>
                    ) : p.restrictions?.range === true ? (
                      <div style={{display:"flex",gap:6,alignItems:"center"}}>
                        <input style={s.input} type="number" value={val?.from||""} placeholder={`od${p.restrictions?.min!=null?` (min ${p.restrictions.min})`:""}`}
                          onChange={e => setParamValues(prev => ({ ...prev, [p.id]: {...(prev[p.id]||{}), from: e.target.value} }))} />
                        <span style={{fontSize:12,color:"#6b7280"}}>–</span>
                        <input style={s.input} type="number" value={val?.to||""} placeholder={`do${p.restrictions?.max!=null?` (max ${p.restrictions.max})`:""}`}
                          onChange={e => setParamValues(prev => ({ ...prev, [p.id]: {...(prev[p.id]||{}), to: e.target.value} }))} />
                      </div>
                    ) : (
                      <input style={s.input} value={val}
                        onChange={e => setParamValues(prev => ({ ...prev, [p.id]: e.target.value }))} />
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 12 }}>
              <MsgSpan k="params" />
              <button onClick={saveParams} disabled={saving === "params"}
                style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
                {saving === "params" ? "⏳..." : "Zapisz parametry"}
              </button>
            </div>
          </div>
        )}

        {/* OPIS */}
        <div style={s.section}>
          <label style={s.label}>Opis oferty (HTML)</label>
          <div style={{ border: "1px solid #d1d5db", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb", padding: "4px 8px", display: "flex", gap: 4 }}>
              <button type="button" onMouseDown={e => { e.preventDefault(); document.execCommand("bold"); }}
                style={{ border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", padding: "2px 8px", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>B</button>
              <button type="button" onMouseDown={e => { e.preventDefault(); document.execCommand("italic"); }}
                style={{ border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", padding: "2px 8px", cursor: "pointer", fontStyle: "italic", fontSize: 12 }}>I</button>
              <button type="button" onMouseDown={e => { e.preventDefault(); document.execCommand("insertUnorderedList"); }}
                style={{ border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", padding: "2px 8px", cursor: "pointer", fontSize: 12 }}>• Lista</button>
              <button type="button" onMouseDown={e => { e.preventDefault(); document.execCommand("insertOrderedList"); }}
                style={{ border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", padding: "2px 8px", cursor: "pointer", fontSize: 12 }}>1. Lista</button>
            </div>
            <div ref={descRef} contentEditable suppressContentEditableWarning
              onInput={e => setDescription((e.target as HTMLDivElement).innerHTML)}
              style={{ minHeight: 200, padding: 12, fontSize: 13, outline: "none", background: "#fff" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 8 }}>
            <MsgSpan k="description" />
            <button onClick={() => saveSection("description", {
              description: { sections: [{ items: [{ type: "TEXT", content: description }] }] }
            })} disabled={saving === "description"}
              style={{ ...s.btn, background: "#1d4ed8", color: "#fff" }}>
              {saving === "description" ? "⏳..." : "Zapisz opis"}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
